import React, { useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChildrenEditor } from "@/components/ChildrenEditor";
import { BonusModelEditor } from "@/components/BonusModelEditor";
import { EmployeeHistory } from "@/components/EmployeeHistory";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserCircle, Archive } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { EmployeePayslipsDialog } from "@/components/EmployeePayslipsDialog";
import { IbanApprovalBanner } from "@/components/IbanApprovalBanner";

export function Employees() {
  const [employees, setEmployees] = useState([]);
  const [payslipEmp, setPayslipEmp] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    street: "",
    postal_code: "",
    city: "",
    ahv_number: "",
    employment_type: "monthly",
    hourly_rate: "",
    vacation_rate: "8.33",
    vacation_days_per_year: "20",
    email: "",
    phone: "",
    base_salary: "",
    pensum_percent: "100",
    qst_rate: "0",
    qst_subject_to_withholding: false,
    birth_date: "",
    iban: "",
    bank_name: "",
    bvg_mode_emp: "default",
    bvg_rate_total_override: "",
    bvg_fixed_amount: "",
    qst_canton: "",
    qst_tariff_code: "",
    // Iter111c: 13. Monatslohn + Kinderzulagen
    has_13th_salary: false,
    thirteenth_mode: "lump_sum",
    children_count: "0",
    children: [],
    bonus_model: null,
    valid_from: "",
    child_allowance_per_child: "200",
  });
  const [qstPreview, setQstPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editingEmployee, setEditingEmployee] = useState(null);

  const companyId = localStorage.getItem("demoCompanyId");

  const [showArchived, setShowArchived] = useState(false);

  const archiveEmployee = async (e) => {
    const exitDate = window.prompt(`${e.first_name} ${e.last_name} archivieren (Austritt).\nAustrittsdatum (JJJJ-MM-TT):`, new Date().toISOString().slice(0, 10));
    if (exitDate === null) return;
    try {
      await apiClient.post(`/employees/${e._id}/archive`, { exit_date: exitDate || null });
      toast.success(`${e.first_name} ${e.last_name} archiviert – keine neuen Lohnläufe, Historie und Lohnausweis bleiben erhalten`);
      await loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Archivieren fehlgeschlagen");
    }
  };

  const unarchiveEmployee = async (e) => {
    try {
      await apiClient.post(`/employees/${e._id}/unarchive`);
      toast.success(`${e.first_name} ${e.last_name} reaktiviert`);
      await loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reaktivieren fehlgeschlagen");
    }
  };

  const loadEmployees = async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get(`/companies/${companyId}/employees`, { params: { include_archived: showArchived } });
      setEmployees(res.data || []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Laden der Mitarbeitenden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, [companyId, showArchived]);

  const handleDelete = async (emp) => {
    setError("");
    try {
      await apiClient.delete(`/employees/${emp._id}`);
      setEmployees((prev) => prev.filter((e) => e._id !== emp._id));
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Löschen des Mitarbeitenden.");
    }
  };

  const handleEditStart = (emp) => {
    setEditingEmployee(emp);
    setForm({
      first_name: emp.first_name,
      last_name: emp.last_name,
      street: emp.street || "",
      postal_code: emp.postal_code || "",
      city: emp.city || "",
      ahv_number: emp.ahv_number || "",
      employment_type: emp.employment_type || "monthly",
      hourly_rate: emp.hourly_rate != null ? String(emp.hourly_rate) : "",
      vacation_rate: String(Math.round(((emp.vacation_rate ?? 0.0833) * 100) * 100) / 100),
      vacation_days_per_year: emp.vacation_days_per_year != null ? String(emp.vacation_days_per_year) : "20",
      email: emp.email || "",
      phone: emp.phone || "",
      base_salary: String(emp.base_salary ?? ""),
      pensum_percent: String(emp.pensum_percent ?? "100"),
      qst_rate: String(emp.qst_rate ?? "0"),
      qst_subject_to_withholding: !!emp.qst_subject_to_withholding,
      birth_date: emp.birth_date || "",
      iban: emp.iban || "",
      bank_name: emp.bank_name || "",
      bvg_mode_emp: emp.bvg_exempt ? "exempt" : emp.bvg_fixed_amount ? "fixed" : emp.bvg_rate_total_override ? "rate" : "default",
      bvg_rate_total_override: emp.bvg_rate_total_override ? String(Math.round(emp.bvg_rate_total_override * 10000) / 100) : "",
      bvg_fixed_amount: emp.bvg_fixed_amount ? String(emp.bvg_fixed_amount) : "",
      qst_canton: emp.qst_canton || "",
      qst_tariff_code: emp.qst_tariff_code || "",
      has_13th_salary: !!emp.has_13th_salary,
      thirteenth_mode: emp.thirteenth_mode || "lump_sum",
      children_count: String(emp.children_count ?? "0"),
      children: Array.isArray(emp.children) ? emp.children : [],
      bonus_model: emp.bonus_model || null,
      child_allowance_per_child: String(emp.child_allowance_per_child ?? "200"),
    });
  };

  const handleEditCancel = () => {
    setEditingEmployee(null);
    setForm({
      first_name: "",
      last_name: "",
      street: "",
      postal_code: "",
      city: "",
      ahv_number: "",
      employment_type: "monthly",
      hourly_rate: "",
      vacation_rate: "8.33",
      email: "",
      phone: "",
      base_salary: "",
      pensum_percent: "100",
      qst_rate: "0",
      qst_subject_to_withholding: false,
      birth_date: "",
      iban: "",
      bank_name: "",
      bvg_mode_emp: "default",
      bvg_rate_total_override: "",
      bvg_fixed_amount: "",
      qst_canton: "",
      qst_tariff_code: "",
      has_13th_salary: false,
      thirteenth_mode: "lump_sum",
      children_count: "0",
    children: [],
      child_allowance_per_child: "200",
    });
    setQstPreview(null);
  };

  // Iter107: Live-Vorschau wenn QST-Pflicht + Canton + Code + Lohn gesetzt
  useEffect(() => {
    const gross = parseFloat(form.base_salary || "0") * (parseFloat(form.pensum_percent || "100") / 100);
    if (!form.qst_subject_to_withholding || !form.qst_canton || !form.qst_tariff_code || gross <= 0) {
      setQstPreview(null);
      return;
    }
    let cancelled = false;
    const tid = setTimeout(async () => {
      try {
        const res = await apiClient.post("/qst/preview", {
          canton: form.qst_canton,
          tariff_code: form.qst_tariff_code,
          gross_monthly: gross,
        });
        if (!cancelled) setQstPreview(res.data);
      } catch (e) {
        if (!cancelled) setQstPreview({ error: e.response?.data?.detail || "Ungültig" });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [form.qst_subject_to_withholding, form.qst_canton, form.qst_tariff_code, form.base_salary, form.pensum_percent]);

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    if (!companyId) return;
    if (!form.first_name || !form.last_name || !form.base_salary) {
      setError("Bitte Vorname, Nachname und Grundlohn angeben.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        company_id: companyId,
        first_name: form.first_name,
        last_name: form.last_name,
        street: form.street || null,
        postal_code: form.postal_code || null,
        city: form.city || null,
        ahv_number: form.ahv_number || null,
        employment_type: form.employment_type,
        hourly_rate: form.employment_type === "hourly" ? (parseFloat(form.hourly_rate) || 0) : null,
        vacation_rate: form.employment_type === "hourly" ? (parseFloat(form.vacation_rate) || 0) / 100 : 0.0833,
        vacation_days_per_year: form.employment_type === "hourly" ? null : (parseFloat(form.vacation_days_per_year) || 20),
        email: form.email || null,
        phone: form.phone || null,
        base_salary: form.employment_type === "hourly" ? 0 : (parseFloat(form.base_salary) || 0),
        pensum_percent: parseFloat(form.pensum_percent) || 100,
        qst_rate: parseFloat(form.qst_rate) || 0,
        qst_subject_to_withholding: !!form.qst_subject_to_withholding,
        birth_date: form.birth_date || null,
        iban: form.iban ? form.iban.replace(/\s+/g, "").toUpperCase() : null,
        bank_name: form.bank_name || null,
        bvg_exempt: form.bvg_mode_emp === "exempt",
        bvg_rate_total_override: form.bvg_mode_emp === "rate" ? (parseFloat(form.bvg_rate_total_override) || 0) / 100 : null,
        bvg_fixed_amount: form.bvg_mode_emp === "fixed" ? (parseFloat(form.bvg_fixed_amount) || 0) : null,
        qst_canton: form.qst_canton || null,
        qst_tariff_code: form.qst_tariff_code || null,
        has_13th_salary: !!form.has_13th_salary,
        thirteenth_mode: form.thirteenth_mode || "lump_sum",
        children: (form.children || []).filter((c) => c.birth_date),
        bonus_model: form.bonus_model && form.bonus_model.active !== undefined ? form.bonus_model : null,
        valid_from: form.valid_from || null,
        children_count: (form.children || []).filter((c) => c.birth_date).length || (parseInt(form.children_count || "0", 10) || 0),
        child_allowance_per_child: parseFloat(form.child_allowance_per_child || "200") || 200,
      };

      if (editingEmployee) {
        await apiClient.put(`/employees/${editingEmployee._id}`, payload);
        toast.success("Mitarbeiter aktualisiert");
      } else {
        const r = await apiClient.post(`/companies/${companyId}/employees`, payload);
        const o = r.data?.onboarding;
        if (o) {
          const ch = [o.email_sent && `E-Mail (${o.email})`, o.whatsapp_sent && `WhatsApp (${o.phone})`].filter(Boolean);
          if (ch.length) toast.success(`Mitarbeiter angelegt – Onboarding-Link sofort per ${ch.join(" + ")} gesendet`, { duration: 8000 });
          else toast.warning(`Mitarbeiter angelegt, Onboarding-Link konnte nicht zugestellt werden: ${o.email_error || o.whatsapp_error || "kein Kanal"}`, { duration: 10000 });
        } else {
          toast.success("Mitarbeiter angelegt – ohne E-Mail/Mobile kein automatisches Onboarding");
        }
      }

      setForm({
        first_name: "",
        last_name: "",
        street: "",
        postal_code: "",
        city: "",
        ahv_number: "",
        employment_type: "monthly",
        hourly_rate: "",
        vacation_rate: "8.33",
        vacation_days_per_year: "20",
        email: "",
        phone: "",
        base_salary: "",
        pensum_percent: "100",
        qst_rate: "0",
        qst_subject_to_withholding: false,
        birth_date: "",
        iban: "",
        bank_name: "",
        bvg_mode_emp: "default",
        bvg_rate_total_override: "",
        bvg_fixed_amount: "",
        qst_canton: "",
        qst_tariff_code: "",
        has_13th_salary: false,
        thirteenth_mode: "lump_sum",
        children_count: "0",
    children: [],
        child_allowance_per_child: "200",
      });
      setEditingEmployee(null);
      await loadEmployees();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Speichern des Mitarbeitenden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Mitarbeitende</h1>
          <p className="mt-1 text-sm text-slate-500">
            Verwalten Sie Mitarbeitende für die Lohnbuchhaltung (vereinfachte Stammdaten).
          </p>
          <label className="mt-2 inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="employees-show-archived-toggle" />
            Archivierte (ausgetretene) Mitarbeitende anzeigen
          </label>
        </div>
        {/* Iter108: Bulk-ZIP-Lohnausweise */}
        <Button
          type="button"
          variant="outline"
          onClick={async () => {
            const yearStr = window.prompt("Jahr für den Bulk-Lohnausweis-Export?", String(new Date().getFullYear()));
            if (!yearStr) return;
            const year = parseInt(yearStr, 10);
            if (!year || year < 2000) {
              toast.error("Ungültiges Jahr");
              return;
            }
            try {
              const res = await apiClient.get(
                `/companies/${companyId}/lohnausweis-bulk`,
                { params: { year }, responseType: "blob" },
              );
              const empCount = res.headers["x-employee-count"] || "?";
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = `Lohnausweise_${year}.zip`;
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
              toast.success(`${empCount} Lohnausweise als ZIP exportiert`);
            } catch (err) {
              toast.error(err.response?.data?.detail || "Bulk-Export fehlgeschlagen");
            }
          }}
          data-testid="employees-bulk-lohnausweis-btn"
        >
          📦 Alle Lohnausweise als ZIP
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={async () => {
            const yearStr = window.prompt("Jahr — Lohnausweise per E-Mail an alle Mitarbeitenden senden?", String(new Date().getFullYear()));
            if (!yearStr) return;
            const year = parseInt(yearStr, 10);
            if (!year || year < 2000) {
              toast.error("Ungültiges Jahr");
              return;
            }
            if (!window.confirm(`Lohnausweise ${year} jetzt per E-Mail an alle Mitarbeitenden mit hinterlegter E-Mail-Adresse verschicken?`)) return;
            try {
              const res = await apiClient.post(
                `/companies/${companyId}/lohnausweis/send-all`, null,
                { params: { year } },
              );
              const d = res.data || {};
              toast.success(`${d.sent_count} Lohnausweis${d.sent_count === 1 ? "" : "e"} ${year} versendet${d.sent?.length ? `: ${d.sent.join(", ")}` : ""}`);
              if (d.no_email?.length) {
                toast.warning(`Keine E-Mail hinterlegt: ${d.no_email.join(", ")} — bitte Stammdaten ergänzen`, { duration: 10000 });
              }
              if (d.failed?.length) {
                toast.error(`Versand fehlgeschlagen: ${d.failed.join(", ")}`);
              }
            } catch (err) {
              toast.error(err.response?.data?.detail || "Versand fehlgeschlagen");
            }
          }}
          data-testid="employees-mail-lohnausweis-btn"
        >
          ✉️ Lohnausweise mailen
        </Button>
      </div>

      <IbanApprovalBanner companyId={companyId} onChanged={loadEmployees} />

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      <Card className="p-4 max-w-3xl border border-slate-200 bg-white shadow-sm" data-testid="employees-form-card">
        <form className="space-y-3" onSubmit={handleCreateOrUpdate} data-testid="employees-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="first-name">Vorname</Label>
              <Input
                id="first-name"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                data-testid="employees-first-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="last-name">Nachname</Label>
              <Input
                id="last-name"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                data-testid="employees-last-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ahv-number">AHV-Nummer</Label>
              <Input
                id="ahv-number"
                value={form.ahv_number}
                onChange={(e) => setForm({ ...form, ahv_number: e.target.value })}
                data-testid="employees-ahv-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="street">Strasse</Label>
              <Input
                id="street"
                value={form.street}
                onChange={(e) => setForm({ ...form, street: e.target.value })}
                data-testid="employees-street-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="postal-code">PLZ</Label>
              <Input
                id="postal-code"
                value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                data-testid="employees-postal-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="city">Ort</Label>
              <Input
                id="city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="employees-city-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="emp-email">E-Mail</Label>
              <Input id="emp-email" type="email" value={form.email} placeholder="für Onboarding, Lohnabrechnung, Login"
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="employees-email-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="emp-phone">Mobile (WhatsApp)</Label>
              <Input id="emp-phone" value={form.phone} placeholder="+41 79 123 45 67"
                onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="employees-phone-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="emp-wage-type">Lohnart</Label>
              <select id="emp-wage-type" value={form.employment_type}
                onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
                className="w-full h-9 text-sm rounded-md border border-slate-200 px-2" data-testid="employees-wage-type-select">
                <option value="monthly">Monatslohn</option>
                <option value="hourly">Stundenlohn</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {form.employment_type === "hourly" ? (
              <>
                <div className="space-y-1">
                  <Label htmlFor="hourly-rate">Stundenansatz (CHF/h)</Label>
                  <Input id="hourly-rate" type="number" step="0.05" value={form.hourly_rate}
                    onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="employees-hourly-rate-input" />
                  <p className="text-[11px] text-slate-500">Die Stunden kommen pro Monat aus der StrongFit-App (oder dem Lohnlauf).</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vacation-rate">Ferienzuschlag (%)</Label>
                  <Input id="vacation-rate" type="number" step="0.01" value={form.vacation_rate}
                    onChange={(e) => setForm({ ...form, vacation_rate: e.target.value })} data-testid="employees-vacation-rate-input" />
                  <p className="text-[11px] text-slate-500">8.33 % = 4 Wochen, 10.64 % = 5 Wochen · 0 wenn der Stundenansatz Ferien bereits enthält</p>
                </div>
              </>
            ) : (
            <div className="space-y-1">
              <Label htmlFor="base-salary">Monatslohn (Brutto)</Label>
              <Input
                id="base-salary"
                type="number"
                value={form.base_salary}
                onChange={(e) => setForm({ ...form, base_salary: e.target.value })}
                data-testid="employees-base-salary-input"
              />
              <button
                type="button"
                className="text-[11px] text-indigo-600 hover:text-indigo-800 underline"
                data-testid="employees-gross-up-btn"
                onClick={async () => {
                  const net = window.prompt("Ziel-NETTOLOHN pro Monat (CHF)?\n\nDas System rechnet mit den aktuellen SV-Sätzen den nötigen Bruttolohn aus.");
                  if (!net || isNaN(Number(net))) return;
                  try {
                    const r = await apiClient.post(`/companies/${companyId}/payroll/gross-up`, {
                      target_net_monthly: Number(net),
                      pensum_percent: parseFloat(form.pensum_percent || "100"),
                      bvg_fixed_amount: form.bvg_fixed_amount ? parseFloat(form.bvg_fixed_amount) : null,
                      qst_rate_percent: parseFloat(form.qst_rate || "0"),
                      qst_subject_to_withholding: !!form.qst_subject_to_withholding,
                      qst_canton: form.qst_canton || null,
                      qst_tariff_code: form.qst_tariff_code || null,
                    });
                    setForm((prev) => ({ ...prev, base_salary: String(r.data.required_base_salary) }));
                    window.alert(`Netto CHF ${Number(net).toFixed(2)} → Brutto CHF ${r.data.required_gross_monthly.toFixed(2)}\nAbzüge total: CHF ${r.data.total_deductions.toFixed(2)} (${r.data.effective_deduction_percent}%)\n\n${r.data.note}\n\nDer Bruttolohn wurde ins Formular übernommen — manuell anpassbar.`);
                  } catch (err) {
                    window.alert(err.response?.data?.detail || "Berechnung fehlgeschlagen");
                  }
                }}
              >
                Netto → Brutto berechnen
              </button>
            </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="pensum">Pensum (%)</Label>
              <Input
                id="pensum"
                type="number"
                value={form.pensum_percent}
                onChange={(e) => setForm({ ...form, pensum_percent: e.target.value })}
                data-testid="employees-pensum-input"
              />
              {form.employment_type !== "hourly" && (
                <>
                  <Label htmlFor="vacation-days" className="pt-1 block">Ferienanspruch (Tage/Jahr)</Label>
                  <Input id="vacation-days" type="number" step="0.5" value={form.vacation_days_per_year}
                    onChange={(e) => setForm({ ...form, vacation_days_per_year: e.target.value })} data-testid="employees-vacation-days-input" />
                  <p className="text-[11px] text-slate-500">20 = 4 Wochen, 25 = 5 Wochen · erscheint als Ferienkonto auf der Lohnabrechnung</p>
                </>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="qst-rate">Quellensteuersatz (%)</Label>
              <Input
                id="qst-rate"
                type="number"
                value={form.qst_rate}
                onChange={(e) => setForm({ ...form, qst_rate: e.target.value })}
                data-testid="employees-qst-rate-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="emp-iban">IBAN (Lohnzahlung)</Label>
              <Input id="emp-iban" value={form.iban} placeholder="CH00 0000 0000 0000 0000 0" onChange={(e) => setForm({ ...form, iban: e.target.value })} data-testid="employees-iban-input" />
              <p className="text-[10px] text-slate-500">erscheint auf der Lohnabrechnung unter «Information zur Auszahlung» und im Zahlungslauf (pain.001)</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="emp-bank">Bank</Label>
              <Input id="emp-bank" value={form.bank_name} placeholder="z.B. UBS Switzerland AG, 6301 Zug" onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="employees-bank-name-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="employees-bvg-section">
            <div className="space-y-1">
              <Label htmlFor="emp-birth">Geburtsdatum</Label>
              <SwissDateField id="emp-birth"  value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="employees-birth-date-input" />
              <p className="text-[10px] text-slate-500">für die BVG-Altersstaffel (25–34: 7 %, 35–44: 10 %, 45–54: 15 %, 55–65: 18 %)</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="emp-bvg-mode">Pensionskasse (BVG)</Label>
              <select id="emp-bvg-mode" value={form.bvg_mode_emp} onChange={(e) => setForm({ ...form, bvg_mode_emp: e.target.value })}
                className="w-full h-9 text-sm rounded-md border border-slate-200 px-2" data-testid="employees-bvg-mode-select">
                <option value="default">Gemäss Lohn-Konfiguration (Schwelle, Alter)</option>
                <option value="exempt">BVG-befreit (unter Schwelle / anderweitig versichert / Rentner)</option>
                <option value="rate">Eigener Satz total AN+AG (%)</option>
                <option value="fixed">Fixbetrag AN pro Monat (CHF)</option>
              </select>
            </div>
            {form.bvg_mode_emp === "rate" && (
              <div className="space-y-1">
                <Label htmlFor="emp-bvg-rate">BVG-Satz total (%)</Label>
                <Input id="emp-bvg-rate" type="number" step="0.1" value={form.bvg_rate_total_override} onChange={(e) => setForm({ ...form, bvg_rate_total_override: e.target.value })} data-testid="employees-bvg-rate-input" />
              </div>
            )}
            {form.bvg_mode_emp === "fixed" && (
              <div className="space-y-1">
                <Label htmlFor="emp-bvg-fixed">BVG Fixbetrag AN (CHF/Monat)</Label>
                <Input id="emp-bvg-fixed" type="number" step="0.05" value={form.bvg_fixed_amount} onChange={(e) => setForm({ ...form, bvg_fixed_amount: e.target.value })} data-testid="employees-bvg-fixed-input" />
              </div>
            )}
          </div>

          {/* Iter111c: 13. Monatslohn + Kinderzulagen */}
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-3" data-testid="employees-phase3-section">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="has-13th"
                checked={form.has_13th_salary}
                onChange={(e) => setForm({ ...form, has_13th_salary: e.target.checked })}
                data-testid="employees-has-13th-checkbox"
                className="h-4 w-4"
              />
              <Label htmlFor="has-13th" className="text-sm font-medium text-emerald-900">
                Anspruch auf 13. Monatslohn
              </Label>
            </div>
            {form.has_13th_salary && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
                <div className="space-y-1">
                  <Label htmlFor="thirteenth-mode" className="text-xs">Auszahlungsmodus</Label>
                  <select
                    id="thirteenth-mode"
                    value={form.thirteenth_mode}
                    onChange={(e) => setForm({ ...form, thirteenth_mode: e.target.value })}
                    data-testid="employees-thirteenth-mode-select"
                    className="w-full border border-slate-300 rounded-md px-2 py-1 text-sm bg-white"
                  >
                    <option value="lump_sum">Einmalig im Dezember</option>
                    <option value="pro_rata">Pro rata (monatlich 1/12)</option>
                  </select>
                </div>
              </div>
            )}

            <ChildrenEditor value={form.children || []} onChange={(children) => setForm({ ...form, children })} testIdPrefix="employees-children" />
            <BonusModelEditor value={form.bonus_model} onChange={(bonus_model) => setForm({ ...form, bonus_model })} />
            {editingEmployee && (
              <div className="space-y-1">
                <Label htmlFor="valid-from">Änderung gültig ab (für Historie)</Label>
                <SwissDateField id="valid-from"  value={form.valid_from || ""} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} data-testid="employees-valid-from-input" />
              </div>
            )}
            {editingEmployee && <EmployeeHistory companyId={companyId} employeeId={editingEmployee._id} />}
            <p className="text-[11px] text-emerald-700 pl-1">
              Kinderzulagen sind AHV-frei, aber steuerbar. 13. Monatslohn unterliegt voller AHV/ALV/BVG-Pflicht.
            </p>
          </div>

          <div className="flex justify-between items-center">
            {editingEmployee ? (
              <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="employees-editing-hint">
                <span>Bearbeiten: <strong>{editingEmployee.first_name} {editingEmployee.last_name}</strong></span>
                <button type="button" className="underline text-slate-500 hover:text-slate-800" onClick={handleEditCancel} data-testid="employees-edit-cancel">Abbrechen</button>
              </div>
            ) : <span />}
            <Button
              type="submit"
              size="sm"
              className="rounded-full bg-slate-900 text-slate-50 px-4"
              disabled={saving}
              data-testid="employees-save-button"
            >
              {saving ? "Speichern ..." : editingEmployee ? "Änderungen speichern" : "Mitarbeitenden erfassen"}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="employees-list-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-700">Mitarbeitende</h2>
          {loading && (
            <span className="text-xs text-slate-400" data-testid="employees-loading">
              Lade ...
            </span>
          )}
        </div>

        <Table data-testid="employees-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Kontakt</TableHead>
              <TableHead>Lohn</TableHead>
              <TableHead>Pensum</TableHead>
              <TableHead>QSt-Satz</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {employees.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="p-0">
                  <EmptyState
                    testid="employees-empty-state"
                    icon={<UserCircle className="h-5 w-5" />}
                    title="Noch keine Mitarbeitenden"
                    description="Legen Sie Mitarbeitende an, um Löhne, AHV/ALV-Abzüge und Lohnabrechnungen automatisch zu berechnen."
                  />
                </TableCell>
              </TableRow>
            )}
            {employees.map((e) => (
              <TableRow key={e._id} data-testid={`employees-row-${e._id}`} className={e.archived_at ? "opacity-60 bg-slate-50" : ""}>
                <TableCell>
                  <div>{e.first_name} {e.last_name}</div>
                  {e.archived_at && (
                    <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700" title={`Archiviert am ${e.archived_at.slice(0, 10)}`} data-testid={`employees-archived-${e._id}`}>
                      Ausgetreten {e.exit_date || ""}
                    </span>
                  )}
                  {e.needs_review && (
                    <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title="Automatisch aus StrongFit angelegt – Stammdaten (Adresse, Geburtsdatum, IBAN, BVG, QST) ergänzen" data-testid={`employees-needs-review-${e._id}`}>
                      Stammdaten ergänzen
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-slate-600">
                  <div>{e.email || <span className="text-amber-700">keine E-Mail</span>}</div>
                  <div>{e.phone || <span className="text-slate-400">kein Mobile</span>}</div>
                  <div className="text-slate-400">{[e.street, [e.postal_code, e.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "keine Adresse"}</div>
                </TableCell>
                <TableCell data-testid={`employees-wage-${e._id}`}>
                  {e.employment_type === "hourly"
                    ? <span>CHF {Number(e.hourly_rate || 0).toFixed(2)}/h <span className="text-[11px] text-slate-500">Stundenlohn{Number(e.vacation_rate || 0) > 0 ? ` · +${(Number(e.vacation_rate) * 100).toFixed(2)} % Ferien` : ""}</span></span>
                    : <span>CHF {Number(e.base_salary || 0).toFixed(2)} <span className="text-[11px] text-slate-500">Monatslohn</span></span>}
                </TableCell>
                <TableCell>{e.pensum_percent.toFixed(1)}%</TableCell>
                <TableCell>{e.qst_rate.toFixed(2)}%</TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-full text-[11px] mr-1 bg-slate-900 text-slate-50"
                    data-testid={`employees-edit-${e._id}`}
                    title="Stammdaten, Lohnart, Stundenansatz, E-Mail, Mobile bearbeiten"
                    onClick={() => { handleEditStart(e); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full text-[11px] mr-1"
                    data-testid={`employees-salary-cert-${e._id}`}
                    title="Lohnausweis für die Steuererklärung (Jahresformular)"
                    onClick={() => window.open(`/salary-certificate/${e._id}?year=${new Date().getFullYear()}`, "_blank")}
                  >
                    Lohnausweis
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full text-[11px] mr-1"
                    data-testid={`employees-onboarding-${e._id}`}
                    title="Onboarding-Link per E-Mail und WhatsApp senden — Mitarbeiter füllt Personalien (Adresse, AHV, IBAN) selbst aus"
                    onClick={async () => {
                      if (!e.email && !e.phone) {
                        window.alert("Bitte zuerst E-Mail und/oder Mobile-Nummer beim Mitarbeiter erfassen (Bearbeiten), damit der Onboarding-Link direkt versendet werden kann.");
                        return;
                      }
                      try {
                        const r = await apiClient.post(`/companies/${companyId}/employees/${e._id}/onboarding-link`);
                        await navigator.clipboard.writeText(r.data.link).catch(() => {});
                        const lines = [`Onboarding-Link für ${r.data.employee_name} erstellt (${r.data.expires_days} Tage gültig, in Zwischenablage kopiert).`, ""];
                        lines.push(r.data.email ? (r.data.email_sent ? `✓ E-Mail an ${r.data.email} gesendet` : `✗ E-Mail an ${r.data.email} fehlgeschlagen (${r.data.email_error || "unbekannt"})`) : "– keine E-Mail hinterlegt");
                        lines.push(r.data.phone ? (r.data.whatsapp_sent ? `✓ WhatsApp an ${r.data.phone} gesendet` : `✗ WhatsApp nicht gesendet (${r.data.whatsapp_error || "unbekannt"})`) : "– keine Mobile-Nummer hinterlegt");
                        if (r.data.phone && !r.data.whatsapp_sent) {
                          lines.push("", "Nachricht stattdessen über dein WhatsApp öffnen?");
                          if (window.confirm(lines.join("\n"))) window.open(r.data.wa_share_url, "_blank");
                        } else {
                          window.alert(lines.join("\n"));
                        }
                        await loadEmployees();
                      } catch (err) {
                        window.alert(err.response?.data?.detail || "Fehlgeschlagen");
                      }
                    }}
                  >
                    {e.onboarding_sent_at ? "Onboarding erneut" : "Onboarding"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={e.portal_enabled ? "default" : "outline"}
                    className={`rounded-full text-[11px] mr-1 ${e.portal_enabled ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                    data-testid={`employees-portal-toggle-${e._id}`}
                    title="Zeiterfassungs-Login für diesen Mitarbeiter"
                    onClick={async () => {
                      try {
                        if (e.portal_enabled) {
                          if (!window.confirm(`Zeiterfassungs-Login für ${e.first_name} ${e.last_name} deaktivieren?`)) return;
                          await apiClient.post(`/companies/${companyId}/employees/${e._id}/portal-access`, { enabled: false });
                        } else {
                          const mail = window.prompt("E-Mail des Mitarbeiters für den Zeiterfassungs-Login:", e.email || "");
                          if (!mail) return;
                          await apiClient.post(`/companies/${companyId}/employees/${e._id}/portal-access`, { enabled: true, email: mail.trim() });
                          window.alert(`Login aktiviert. ${mail.trim()} kann sich jetzt auf der Login-Seite per E-Mail-Code anmelden und sieht nur die eigene Zeiterfassung.`);
                        }
                        await loadEmployees();
                      } catch (err) {
                        window.alert(err.response?.data?.detail || "Fehlgeschlagen");
                      }
                    }}
                  >
                    {e.portal_enabled ? "Login aktiv" : "Login"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full text-[11px]"
                    data-testid={`employees-lohnausweis-${e._id}`}
                    onClick={async () => {
                      const year = new Date().getFullYear();
                      try {
                        const res = await apiClient.get(
                          `/companies/${companyId}/employees/${e._id}/lohnausweis`,
                          { params: { year }, responseType: "blob" },
                        );
                        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `Lohnausweis_${year}_${e.last_name}.pdf`;
                        document.body.appendChild(a); a.click(); document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                  >
                    Lohnausweis {new Date().getFullYear()}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full text-[11px] ml-1"
                    data-testid={`employees-payslips-${e._id}`}
                    title="Archivierte Lohnabrechnungen (PDF) ansehen und herunterladen"
                    onClick={() => setPayslipEmp(e)}
                  >
                    Abrechnungen
                  </Button>
                  {e.archived_at ? (
                    <Button type="button" size="sm" variant="outline" className="rounded-full text-[11px] ml-1 border-emerald-300 text-emerald-800"
                      data-testid={`employees-unarchive-${e._id}`} title="Mitarbeiter reaktivieren (arbeitet wieder bei uns)" onClick={() => unarchiveEmployee(e)}>
                      Reaktivieren
                    </Button>
                  ) : (
                    <Button type="button" size="sm" variant="outline" className="rounded-full text-[11px] ml-1 border-slate-300 text-slate-600 hover:text-red-700 hover:border-red-300"
                      data-testid={`employees-archive-${e._id}`} title="Austritt: Mitarbeiter archivieren – keine neuen Lohnläufe, Lohnausweis/Historie bleiben" onClick={() => archiveEmployee(e)}>
                      <Archive className="h-3 w-3 mr-1" />Archivieren
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <EmployeePayslipsDialog employee={payslipEmp} companyId={companyId} onClose={() => setPayslipEmp(null)} />
    </div>
  );
}
